import React, { useMemo, useState } from "react";

const bioPeriodicTableCss = `
.bio-periodic-shell {
  width: 100%;
  padding: 22px;
  border-radius: 24px;
  border: 1px solid #cddcec;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.08), transparent 32%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 16px 44px rgba(7, 17, 31, 0.065);
  color: #07111f;
}

.bio-periodic-shell * {
  box-sizing: border-box;
}

.bio-periodic-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.bio-periodic-head p {
  margin: 0 0 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.bio-periodic-head h2 {
  margin: 0;
  color: #07111f;
  font-size: clamp(26px, 3vw, 34px);
  line-height: 1.05;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.bio-periodic-head span {
  display: block;
  margin-top: 8px;
  max-width: 760px;
  color: #66778e;
  font-size: 13px;
  line-height: 1.55;
}

.bio-periodic-filters {
  display: grid;
  grid-template-columns: minmax(170px, 1fr) 170px;
  gap: 10px;
  min-width: 390px;
}

.bio-periodic-filters input,
.bio-periodic-filters select {
  width: 100%;
  min-height: 42px;
  padding: 10px 13px;
  border-radius: 12px;
  border: 1px solid #cddcec;
  background: rgba(255, 255, 255, 0.98);
  color: #07111f;
  font-size: 13px;
  font-weight: 800;
  outline: none;
}

.bio-periodic-filters input:focus,
.bio-periodic-filters select:focus {
  border-color: rgba(31, 123, 193, 0.72);
  box-shadow: 0 0 0 4px rgba(24, 183, 255, 0.12);
}

.bio-periodic-grid-wrap {
  overflow: auto;
  padding: 10px;
  border-radius: 20px;
  background:
    radial-gradient(circle at 52% 38%, rgba(24, 183, 255, 0.10), transparent 36%),
    linear-gradient(180deg, #f8fbff, #eef6ff);
  border: 1px solid #d9e8f6;
}

.bio-periodic-grid {
  display: grid;
  grid-template-columns: repeat(18, 58px);
  grid-template-rows: repeat(10, 58px);
  gap: 7px;
  min-width: 1160px;
}

.bio-element-cell {
  border: 1px solid #d3e2f2;
  border-radius: 12px;
  background: #ffffff;
  color: #07111f;
  display: grid;
  align-content: center;
  justify-items: center;
  padding: 6px;
  cursor: pointer;
  transition: 0.16s ease;
  box-shadow: 0 8px 18px rgba(7, 17, 31, 0.035);
}

.bio-element-cell:hover {
  transform: translateY(-2px);
  border-color: #18b7ff;
  box-shadow:
    0 0 0 2px rgba(24, 183, 255, 0.10),
    0 14px 28px rgba(31, 123, 193, 0.12);
}

.bio-element-cell.selected {
  background:
    radial-gradient(circle at 30% 15%, rgba(255, 255, 255, 0.24), transparent 32%),
    linear-gradient(135deg, #1f7bc1, #0f5f9d);
  color: #ffffff;
  border-color: #0f5f9d;
  box-shadow:
    0 0 0 2px rgba(24, 183, 255, 0.16),
    0 14px 28px rgba(31, 123, 193, 0.20);
}

.bio-element-cell.muted {
  opacity: 0.25;
}

.bio-element-cell span {
  font-size: 9px;
  font-weight: 850;
  opacity: 0.75;
}

.bio-element-cell strong {
  font-size: 18px;
  line-height: 1;
  font-weight: 950;
}

.bio-element-cell small {
  max-width: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 9px;
  font-weight: 750;
  opacity: 0.74;
}

.bio-selected-elements {
  margin-top: 14px;
  padding: 14px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.bio-selected-elements strong {
  color: #07111f;
  font-size: 13px;
  font-weight: 950;
}

.bio-selected-elements span {
  color: #66778e;
  font-size: 13px;
}

.bio-selected-elements em {
  font-style: normal;
  padding: 5px 9px;
  border-radius: 999px;
  background: rgba(31, 123, 193, 0.10);
  color: #0b4e84;
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 12px;
  font-weight: 900;
}

@media (max-width: 980px) {
  .bio-periodic-head {
    display: grid;
  }

  .bio-periodic-filters {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 760px) {
  .bio-periodic-shell {
    padding: 16px;
    border-radius: 20px;
  }

  .bio-periodic-filters {
    grid-template-columns: 1fr;
  }

  .bio-periodic-grid-wrap {
    padding: 8px;
    border-radius: 18px;
  }

  .bio-periodic-grid {
    grid-template-columns: repeat(18, 52px);
    grid-template-rows: repeat(10, 52px);
    gap: 6px;
    min-width: 1040px;
  }

  .bio-element-cell {
    border-radius: 11px;
  }

  .bio-element-cell strong {
    font-size: 16px;
  }
}
`;

export default function BioPeriodicTable({
  elements = [],
  selectedElements = [],
  onToggleElement,
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const selectedSymbols = useMemo(() => {
    return selectedElements.map((item) => item.symbol);
  }, [selectedElements]);

  const categories = useMemo(() => {
    const values = Array.from(
      new Set(elements.map((element) => element.category).filter(Boolean))
    );

    return ["all", ...values.sort()];
  }, [elements]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return elements.filter((element) => {
      const matchesCategory =
        category === "all" ||
        String(element.category || "").toLowerCase() === category.toLowerCase();

      const matchesQuery =
        !q ||
        String(element.name || "").toLowerCase().includes(q) ||
        String(element.symbol || "").toLowerCase().includes(q) ||
        String(element.atomic_number || "").includes(q);

      return matchesCategory && matchesQuery;
    });
  }, [elements, query, category]);

  const visibleIds = useMemo(() => {
    return new Set(filtered.map((item) => item.id));
  }, [filtered]);

  return (
    <section className="bio-periodic-shell">
      <style>{bioPeriodicTableCss}</style>

      <div className="bio-periodic-head">
        <div>
          <p>Element Explorer</p>
          <h2>Periodic Table</h2>
          <span>
            Select elements for compound building, atom-level graph design, and
            digital avatar hypothesis mapping.
          </span>
        </div>

        <div className="bio-periodic-filters">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search element..."
          />

          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            {categories.map((item) => (
              <option key={item} value={item}>
                {item === "all" ? "All Categories" : item}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bio-periodic-grid-wrap">
        <div className="bio-periodic-grid">
          {elements.map((element) => {
            const selected = selectedSymbols.includes(element.symbol);
            const visible = visibleIds.has(element.id);

            return (
              <button
                key={element.id}
                type="button"
                className={[
                  "bio-element-cell",
                  selected ? "selected" : "",
                  visible ? "" : "muted",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{
                  gridColumn: element.display_column || 1,
                  gridRow: element.display_row || 1,
                }}
                onClick={() => onToggleElement?.(element)}
                title={`${element.name || "Element"} (${element.symbol || "-"})`}
              >
                <span>{element.atomic_number}</span>
                <strong>{element.symbol}</strong>
                <small>{element.name}</small>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bio-selected-elements">
        <strong>Selected Elements:</strong>

        {selectedElements.length === 0 ? (
          <span>No elements selected</span>
        ) : (
          selectedElements.map((element) => (
            <em key={element.id}>{element.symbol}</em>
          ))
        )}
      </div>
    </section>
  );
}